/**
 * **The targeting-mode hover forecast** (`projects/target-hover-forecast.md` 3.2): while an aim is
 * armed and the pointer is over a unit it may legally target, a small card beside the pointer lists
 * what the shot would roll. It leads with the hit chance (`63% HIT`, the net `−2 DIFF` tagged beside
 * it), or with the net when the viewer's mask hides the hit chance (`forecast-card-headline`). Below
 * that: every accuracy and difficulty source with its name, the roll as the dice run
 * (`1d20 +1 −best of 2d6 vs EVASION 5`) with the crit chance, what Armor does to the hit, the
 * pre-roll riders and the warnings.
 *
 * The numbers are `@lancer-vtt/rules`' `viewAttackForecast` (`forecastAttack`, the pre-roll twin of
 * the resolution's S1-S3, then `maskAttackForecast` for this viewer) and its wording is
 * `attackForecastRows`, the same rows `lvtt encounter forecast-attack` prints. This file only lays
 * them out. What the viewer does not know prints `??`, and the hit chance, derived from the
 * defence, prints `??` too, a nudge that a Scan would reveal it.
 *
 * `intent` is the `request_attack` the click would send (`attackIntentForAim`), so the forecast and
 * the committed shot are one intent. `null`, or an attack the forecast cannot describe (an area
 * weapon, a non-rolling tech action), renders nothing. The card never takes the pointer: the click
 * still lands on the token and goes through `CombatApp.fireAt`.
 *
 * Mount it for the whole time targeting mode is on, not only while a target is hovered: it anchors
 * at the last pointer position its own `pointermove` / `pointerdown` listeners saw, flipped to stay
 * on the canvas. On touch the card opens on a first tap and the second tap commits (`targetTap.ts`).
 */
import { faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import { Icon } from "../Icon";
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import type { ActorRole, EncounterState } from "@lancer-vtt/domain";
import { attackForecastRows, viewAttackForecast, type LoadedOverlay } from "@lancer-vtt/rules";
import type { RequestAttack } from "@lancer-vtt/shared";
import { TARGETING_RGB } from "../../routes/home/pixiCombatBoard";
import "./target-forecast-panel.css";

/** Distance from the pointer to the card's near corner, and the card's minimum margin from the canvas edge. */
const POINTER_GAP_PX = 18;
const EDGE_PX = 8;

export type TargetForecastPanelProps = {
  state: EncounterState;
  /** The merged action overlay. Absent while the world sidecar is still loading: the card waits for it. */
  overlay: LoadedOverlay | undefined;
  /** The `request_attack` a click on the hovered target would send, or `null` when none is hovered. */
  intent: RequestAttack | null;
  viewer: { role: ActorRole; viewerSlot: string | null };
};

/** Place a `w`×`h` card beside the pointer inside a `bw`×`bh` box, flipping left or up when it would overflow. */
export function forecastPlacement(args: { px: number; py: number; w: number; h: number; bw: number; bh: number }): { left: number; top: number; flipX: boolean; flipY: boolean } {
  const { px, py, w, h, bw, bh } = args;
  const flipX = px + POINTER_GAP_PX + w > bw - EDGE_PX;
  const flipY = py + POINTER_GAP_PX + h > bh - EDGE_PX;
  const left = flipX ? px - POINTER_GAP_PX - w : px + POINTER_GAP_PX;
  const top = flipY ? py - POINTER_GAP_PX - h : py + POINTER_GAP_PX;
  return { left: Math.max(EDGE_PX, left), top: Math.max(EDGE_PX, top), flipX, flipY };
}

export function TargetForecastPanel({ state, overlay, intent, viewer }: TargetForecastPanelProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [pointer, setPointer] = useState<{ x: number; y: number } | null>(null);

  useEffect(() => {
    const onMove = (e: PointerEvent) => setPointer({ x: e.clientX, y: e.clientY });
    window.addEventListener("pointermove", onMove, { passive: true });
    // A tap moves no pointer: the press itself anchors the card a first touch tap expands (`targetTap.ts`).
    window.addEventListener("pointerdown", onMove, { passive: true, capture: true });
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerdown", onMove, { capture: true });
    };
  }, []);

  // The memo key is the intent's content: `CombatApp` rebuilds the object every render.
  const intentKey = intent ? JSON.stringify(intent) : null;
  const view = useMemo(() => {
    if (!intent || intent.target.kind !== "unit" || overlay === undefined) return null;
    const v = viewAttackForecast({
      state,
      overlay,
      viewer,
      attack: {
        attackerId: intent.attacker,
        targetId: intent.target.id,
        accuracy: intent.accuracy,
        ...(intent.weapon !== undefined ? { weapon: intent.weapon } : {}),
        ...(intent.profile !== undefined ? { profile: intent.profile } : {}),
        ...(intent.tech ? { tech: true } : {}),
        ...(intent.choices !== undefined ? { choices: intent.choices } : {}),
      },
    });
    return v.ok ? { ...v, rows: attackForecastRows(state, v.forecast) } : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the intent's content, not its identity
  }, [state, overlay, intentKey, viewer.role, viewer.viewerSlot]);

  useLayoutEffect(() => {
    const el = ref.current;
    const box = el?.offsetParent ?? el?.parentElement;
    if (!el || !box || !pointer) return;
    const rect = box.getBoundingClientRect();
    const p = forecastPlacement({ px: pointer.x - rect.left, py: pointer.y - rect.top, w: el.offsetWidth, h: el.offsetHeight, bw: rect.width, bh: rect.height });
    el.style.left = `${p.left}px`;
    el.style.top = `${p.top}px`;
    el.dataset.flipX = String(p.flipX);
    el.dataset.flipY = String(p.flipY);
  }, [pointer, view]);

  if (!view || !intent || intent.target.kind !== "unit") return null;
  const { forecast: f, rows, dependsOn } = view;
  const rgb = { "--tfp-rgb": TARGETING_RGB[f.techAttack ? "tech" : "attack"] } as CSSProperties;
  const sign = (n: number) => (n > 0 ? "acc" : n < 0 ? "diff" : "flat");

  return (
    <div
      ref={ref}
      className="tfp"
      style={rgb}
      role="tooltip"
      data-testid="target-forecast"
      data-target={f.targetId}
      data-net={f.net}
      data-defence-vs={f.defence.vs}
      data-defence-value={rows.defence.valueText}
      data-hit={rows.hitChance.hit}
      data-depends-on={dependsOn?.promptId}
    >
      <div className="tfp-head">
        {rows.headline.tag === undefined ? (
          <span className="tfp-headline tfp-net" data-sign={sign(f.net)}>
            {rows.headline.text}
          </span>
        ) : (
          <>
            <span className="tfp-headline" data-testid="target-forecast-hit">
              {rows.headline.text}
            </span>
            <span className="tfp-net" data-sign={sign(f.net)}>
              {rows.headline.tag}
            </span>
          </>
        )}
      </div>
      {rows.contributions.length > 0 && (
        <ul className="tfp-contribs">
          {rows.contributions.map((c, i) => (
            <li key={`${c.kind}:${c.name}:${i}`} className="tfp-contrib" data-kind={c.kind} data-sign={sign(c.value)}>
              <span className="tfp-contrib-name">
                {c.name}
                {c.via !== undefined && <span className="tfp-via"> via {c.via}</span>}
              </span>
              <span className="tfp-contrib-val">{c.valueText}</span>
            </li>
          ))}
        </ul>
      )}
      <div className="tfp-roll">
        <span className="tfp-dice">
          {rows.rollText}{" "}
          <span className="tfp-vs" data-unknown={rows.defence.valueText === "??" || undefined}>
            vs {rows.defence.label} {rows.defence.valueText}
          </span>
        </span>
        {rows.headline.tag === undefined ? (
          <span className="tfp-odds" data-testid="target-forecast-hit" data-unknown>
            HIT {rows.hitChance.hit} · CRIT {rows.hitChance.crit}
          </span>
        ) : (
          <span className="tfp-odds">CRIT {rows.hitChance.crit}</span>
        )}
      </div>
      {rows.defence.capNote !== undefined && <div className="tfp-note">{rows.defence.capNote}</div>}
      {rows.damage?.length !== 0 && (
        <div className="tfp-dmg">
          {rows.damage === null ? <span className="tfp-chip">NO WEAPON DAMAGE</span> : rows.damage.map((d) => <span key={d} className="tfp-chip">{d}</span>)}
        </div>
      )}
      {(rows.riders.length > 0 || dependsOn !== undefined || rows.warnings.length > 0) && (
        <ul className="tfp-notes">
          {rows.riders.map((r) => (
            <li key={r} className="tfp-rider">
              {r}
            </li>
          ))}
          {dependsOn !== undefined && <li className="tfp-depends">depends on “{dependsOn.prompt}”</li>}
          {rows.warnings.map((w) => (
            <li key={w} className="tfp-warning">
              <Icon icon={faTriangleExclamation} className="icon-lead" />{w}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
